import { Skeleton as PrimitiveSkeleton } from './Skeleton';
import { Surface } from './Surface';

export { PrimitiveSkeleton as Skeleton };

const LoadingRegion = ({ children }: { children: React.ReactNode }) => (
  <div role="status" aria-label="Loading content" aria-live="polite">
    <span className="sr-only">Loading content</span>
    <div aria-hidden="true">{children}</div>
  </div>
);

export function CardSkeleton() {
  return (
    <Surface padding="lg">
      <LoadingRegion>
        <div className="grid gap-4">
          <PrimitiveSkeleton shape="title" />
          <PrimitiveSkeleton />
          <PrimitiveSkeleton className="w-4/5" />
          <div className="mt-2 flex gap-inline-control-gap">
            <PrimitiveSkeleton shape="control" className="max-w-28" />
            <PrimitiveSkeleton shape="control" className="max-w-28" />
          </div>
        </div>
      </LoadingRegion>
    </Surface>
  );
}

export function TableSkeleton({ rows = 5 }: { rows?: number }) {
  return (
    <LoadingRegion>
      <div className="grid gap-3 rounded-surface border border-border-subtle bg-surface p-4">
        <PrimitiveSkeleton shape="title" />
        {Array.from({ length: rows }).map((_, index) => (
          <div key={index} className="grid grid-cols-4 gap-4 border-t border-border-subtle pt-3">
            <PrimitiveSkeleton />
            <PrimitiveSkeleton />
            <PrimitiveSkeleton />
            <PrimitiveSkeleton />
          </div>
        ))}
      </div>
    </LoadingRegion>
  );
}

export function StatsGridSkeleton({ count = 4 }: { count?: number }) {
  return (
    <LoadingRegion>
      <div className="grid grid-cols-1 gap-region-gap sm:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: count }).map((_, index) => (
          <Surface key={index} padding="md">
            <div className="grid gap-3">
              <PrimitiveSkeleton className="w-3/5" />
              <PrimitiveSkeleton shape="title" className="w-2/5" />
              <PrimitiveSkeleton className="w-4/5" />
            </div>
          </Surface>
        ))}
      </div>
    </LoadingRegion>
  );
}

export function ChartSkeleton() {
  return (
    <Surface padding="lg">
      <LoadingRegion>
        <div className="grid gap-4">
          <PrimitiveSkeleton shape="title" className="w-2/5" />
          <PrimitiveSkeleton shape="block" className="h-64" />
        </div>
      </LoadingRegion>
    </Surface>
  );
}

export function ListSkeleton({ items = 5 }: { items?: number }) {
  return (
    <LoadingRegion>
      <div className="grid gap-4">
        {Array.from({ length: items }).map((_, index) => (
          <Surface key={index} padding="md">
            <div className="flex items-center gap-4">
              <PrimitiveSkeleton shape="circle" />
              <div className="grid flex-1 gap-2">
                <PrimitiveSkeleton className="w-2/5" />
                <PrimitiveSkeleton className="w-3/5" />
              </div>
            </div>
          </Surface>
        ))}
      </div>
    </LoadingRegion>
  );
}

export function DeviceCardSkeleton() {
  return (
    <Surface padding="sm">
      <LoadingRegion>
        <div className="flex items-start gap-field-gap">
          <PrimitiveSkeleton shape="circle" />
          <div className="grid flex-1 gap-2">
            <PrimitiveSkeleton className="w-3/4" />
            <PrimitiveSkeleton className="w-1/2" />
            <PrimitiveSkeleton className="w-2/5" />
          </div>
        </div>
      </LoadingRegion>
    </Surface>
  );
}

export function DashboardSkeleton() {
  return (
    <div className="mx-auto grid max-w-content gap-page-gap px-page-inline-compact py-8 sm:px-page-inline-sm lg:px-page-inline-lg">
      <LoadingRegion>
        <div className="grid gap-2">
          <PrimitiveSkeleton shape="title" className="max-w-52" />
          <PrimitiveSkeleton className="max-w-80" />
        </div>
      </LoadingRegion>
      <StatsGridSkeleton />
      <div className="grid gap-region-gap lg:grid-cols-2">
        <ChartSkeleton />
        <ChartSkeleton />
      </div>
      <ListSkeleton items={3} />
    </div>
  );
}

export function ModelsSkeleton() {
  return (
    <div className="mx-auto grid max-w-content gap-page-gap px-page-inline-compact py-8 sm:px-page-inline-sm lg:px-page-inline-lg">
      <LoadingRegion>
        <PrimitiveSkeleton shape="title" className="max-w-52" />
      </LoadingRegion>
      <div className="grid grid-cols-1 gap-region-gap md:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: 6 }).map((_, index) => (
          <CardSkeleton key={index} />
        ))}
      </div>
    </div>
  );
}

export function TrainingSkeleton() {
  return (
    <div className="mx-auto grid max-w-content gap-page-gap px-page-inline-compact py-8 sm:px-page-inline-sm lg:px-page-inline-lg">
      <LoadingRegion>
        <PrimitiveSkeleton shape="title" className="max-w-52" />
      </LoadingRegion>
      <StatsGridSkeleton />
      <ListSkeleton items={4} />
    </div>
  );
}

export function MonitoringSkeleton() {
  return (
    <div className="mx-auto grid max-w-content gap-region-gap px-page-inline-compact py-8 sm:px-page-inline-sm lg:grid-cols-[17rem_minmax(0,1fr)] lg:px-page-inline-lg">
      <div className="grid content-start gap-4">
        {Array.from({ length: 4 }).map((_, index) => (
          <DeviceCardSkeleton key={index} />
        ))}
      </div>
      <div className="grid content-start gap-region-gap">
        <ChartSkeleton />
        <CardSkeleton />
      </div>
    </div>
  );
}

interface LoadingSkeletonProps {
  count?: number;
  height?: string;
}

export default function LoadingSkeleton({ count = 3, height = '20px' }: LoadingSkeletonProps) {
  const heightClass = height === '16px' ? 'h-4' : 'h-5';
  return (
    <LoadingRegion>
      <div className="grid gap-3">
        {Array.from({ length: count }).map((_, index) => (
          <PrimitiveSkeleton key={index} className={heightClass} />
        ))}
      </div>
    </LoadingRegion>
  );
}
